import type React from 'react'
import { createContext, useContext, useState, useMemo } from 'react'
import { useSupplierReconciliation } from '../../../../../../api/useSupplierReconciliation'
import type { RecMatchRow } from '../../../../../../types/api/v1'
import { getDaysAgo } from '../utils/getDaysAgo'
import dayjs from 'dayjs'

// Types for supplier breakdown
interface SupplierBreakdown {
  supplierId: string
  supplierName: string
  count: number
}

interface MatchCounts {
  unmatched: number
  unapproved: number
  onHold: number
}

interface SupplierBreakdowns {
  unmatched: SupplierBreakdown[]
  unapproved: SupplierBreakdown[]
  onHold: SupplierBreakdown[]
}

// Simplified context interface
interface LaunchpadContextValue {
  // State
  assignedToMe: boolean
  setAssignedToMe: (value: boolean) => void

  // Data
  isLoading: boolean
  matches: RecMatchRow[]

  // Metrics
  metrics: {
    autoApprovePercentage: number
    totalPastDue: number
    totalOpenItems: number
    totalUnmatchedItems: number
  }

  // Trends (14-day) - percentage change
  trends: {
    autoApprovePercentage: number
    totalPastDue: number
    totalOpenItems: number
    totalUnmatchedItems: number
  }

  // Match counts and breakdowns
  matchCounts: MatchCounts
  supplierBreakdowns: SupplierBreakdowns
}

// Create context
const LaunchpadContext = createContext<LaunchpadContextValue | undefined>(undefined)

// Helper function to generate auto-approve percentage with 85-92% average
function generateAutoApprovePercentage(dayOffset: number): number {
  // Base percentage between 85-92%
  const base = 88.5 // Middle of 85-92 range
  const variation = Math.sin(dayOffset * 0.8) * 3.5 + Math.random() * 2 - 1
  return Math.max(85, Math.min(92, base + variation))
}

// Helper function to calculate trend between two values
function calculateTrend(oldValue: number, newValue: number): number {
  if (oldValue === 0) return newValue > 0 ? 100 : 0
  return ((newValue - oldValue) / oldValue) * 100
}

// Fixed 30-day period for all metrics (consistent with SharedHomeContext)
const FIXED_DAYS = 30

// Main provider component
export function LaunchpadProvider({
  children,
}: {
  children: React.ReactNode
}) {
  // Get API hooks
  const { useMatchOverviewOnlyOpen } = useSupplierReconciliation()

  // State for assigned to me toggle
  const [assignedToMe, setAssignedToMe] = useState(false)

  // Fixed 30-day period - memoize to prevent re-renders
  const { currentPeriodStart, currentPeriodEnd } = useMemo(
    () => ({
      currentPeriodStart: getDaysAgo(FIXED_DAYS),
      currentPeriodEnd: new Date(),
    }),
    []
  ) // Empty deps - only calculate once on mount

  // Fetch data for current period
  const queryParams = useMemo(
    () => ({
      dateRange: {
        start: currentPeriodStart,
        end: currentPeriodEnd,
      },
      filters: {}, // We'll filter client-side for now
    }),
    [currentPeriodStart, currentPeriodEnd]
  )

  const { data: matchViewData, isLoading } = useMatchOverviewOnlyOpen(queryParams)

  // Extract and filter matches
  const matches = useMemo(() => {
    if (!matchViewData?.rows) return []

    const allMatches = matchViewData.rows as RecMatchRow[]

    // Note: RecMatchRow doesn't have assignee field based on the API type
    // Filter logic would need to be implemented server-side or added to the type
    if (assignedToMe) {
      // For now, return all matches when assignedToMe is true
      // This would need backend support to filter properly
      return allMatches
    }

    return allMatches
  }, [matchViewData, assignedToMe])

  // Calculate real-time metrics
  const metrics = useMemo(() => {
    const now = dayjs()

    // Auto-Approve percentage (fake but realistic)
    const autoApprovePercentage = generateAutoApprovePercentage(0)

    // Total Past Due (sum of invoice totals where due date has passed)
    let totalPastDue = 0
    matches.forEach((match) => {
      // Skip voided matches entirely
      if (match.status === 'void') {
        return
      }

      // Check if invoice is past due based on invoice_due_date
      if (match.invoice_due_date) {
        const dueDate = dayjs(match.invoice_due_date)
        const daysPastDue = now.diff(dueDate, 'day')
        if (daysPastDue >= 0) {
          totalPastDue += match.invoice_total || 0
        }
      }
    })

    // Total Open Items (count of all non-approved invoices)
    let totalOpenItems = 0
    matches.forEach((match) => {
      // Skip voided matches entirely
      if (match.status === 'void') {
        return
      }

      if (match.status !== 'approved') {
        // Count the number of invoices in this match
        totalOpenItems += match.invoice_numbers?.length || 0
      }
    })

    // Total Unmatched Items (count of matches with 'unmatched' status)
    const totalUnmatchedItems = matches.filter(
      (match) => match.status === 'unmatched' && match.status !== 'void'
    ).length

    return {
      autoApprovePercentage,
      totalPastDue,
      totalOpenItems,
      totalUnmatchedItems,
    }
  }, [matches])

  // Calculate 14-day trends (fake but realistic)
  const trends = useMemo(() => {
    // Simulate day 1 values (14 days ago)
    const day1AutoApprove = generateAutoApprovePercentage(13)
    const day1PastDue = metrics.totalPastDue * 0.85 // Assume 15% increase
    const day1OpenItems = metrics.totalOpenItems * 0.9 // Assume 10% increase
    const day1Unmatched = metrics.totalUnmatchedItems * 1.2 // Assume 20% decrease

    return {
      autoApprovePercentage: calculateTrend(day1AutoApprove, metrics.autoApprovePercentage),
      totalPastDue: calculateTrend(day1PastDue, metrics.totalPastDue),
      totalOpenItems: calculateTrend(day1OpenItems, metrics.totalOpenItems),
      totalUnmatchedItems: calculateTrend(day1Unmatched, metrics.totalUnmatchedItems),
    }
  }, [metrics])

  // Calculate match counts and supplier breakdowns
  const { matchCounts, supplierBreakdowns } = useMemo(() => {
    // Group matches by supplier and status
    const supplierStatusMap = new Map<string, Map<string, number>>()

    matches.forEach((match) => {
      // Skip voided matches entirely
      if (match.status === 'void') {
        return
      }

      const supplierId = match.supplier_id
      const supplierName = match.supplier_name
      const status = match.status

      if (!supplierStatusMap.has(supplierId)) {
        supplierStatusMap.set(supplierId, new Map())
      }

      const statusMap = supplierStatusMap.get(supplierId)!
      statusMap.set('name', supplierName)
      statusMap.set(status, (statusMap.get(status) || 0) + 1)
    })

    // Calculate total counts
    let unmatchedCount = 0
    let unapprovedCount = 0
    let onHoldCount = 0

    // Build supplier breakdowns
    const unmatchedSuppliers: SupplierBreakdown[] = []
    const unapprovedSuppliers: SupplierBreakdown[] = []
    const onHoldSuppliers: SupplierBreakdown[] = []

    supplierStatusMap.forEach((statusMap, supplierId) => {
      const supplierName = statusMap.get('name') as string

      const unmatched = statusMap.get('unmatched') || 0
      const unapproved = statusMap.get('unapproved') || 0
      const hold = statusMap.get('hold') || 0

      if (unmatched > 0) {
        unmatchedCount += unmatched
        unmatchedSuppliers.push({ supplierId, supplierName, count: unmatched })
      }

      if (unapproved > 0) {
        unapprovedCount += unapproved
        unapprovedSuppliers.push({ supplierId, supplierName, count: unapproved })
      }

      if (hold > 0) {
        onHoldCount += hold
        onHoldSuppliers.push({ supplierId, supplierName, count: hold })
      }
    })

    // Sort suppliers by count (descending) and take top 5
    const sortByCount = (a: SupplierBreakdown, b: SupplierBreakdown) => b.count - a.count

    return {
      matchCounts: {
        unmatched: unmatchedCount,
        unapproved: unapprovedCount,
        onHold: onHoldCount,
      },
      supplierBreakdowns: {
        unmatched: unmatchedSuppliers.sort(sortByCount).slice(0, 5),
        unapproved: unapprovedSuppliers.sort(sortByCount).slice(0, 5),
        onHold: onHoldSuppliers.sort(sortByCount).slice(0, 5),
      },
    }
  }, [matches])

  const value: LaunchpadContextValue = {
    assignedToMe,
    setAssignedToMe,
    isLoading,
    matches,
    metrics,
    trends,
    matchCounts,
    supplierBreakdowns,
  }

  return <LaunchpadContext.Provider value={value}>{children}</LaunchpadContext.Provider>
}

// Hook to use the context (now accepts matches as parameter for standalone usage)
export function useLaunchpad(providedMatches?: RecMatchRow[]) {
  // If used within a provider, get context
  const context = useContext(LaunchpadContext)

  // If providedMatches are given, calculate metrics directly (for use with shared context)
  if (providedMatches !== undefined) {
    return calculateMetricsFromMatches(providedMatches)
  }

  // Otherwise, use context
  if (!context) {
    throw new Error('useLaunchpad must be used within a LaunchpadProvider or provide matches as parameter')
  }
  return context
}

// Helper function to calculate metrics from matches
function calculateMetricsFromMatches(matches: RecMatchRow[]) {
  // Calculate real-time metrics
  const metrics = useMemo(() => {
    const now = dayjs()

    // Auto-Approve percentage (fake but realistic)
    const autoApprovePercentage = generateAutoApprovePercentage(0)

    // Total Past Due (sum of invoice totals where due date has passed)
    let totalPastDue = 0
    matches.forEach((match) => {
      // Skip voided matches entirely
      if (match.status === 'void') {
        return
      }

      // Check if invoice is past due based on invoice_due_date
      if (match.invoice_due_date) {
        const dueDate = dayjs(match.invoice_due_date)
        const daysPastDue = now.diff(dueDate, 'day')
        if (daysPastDue >= 0) {
          totalPastDue += match.invoice_total || 0
        }
      }
    })

    // Total Open Items (count of all non-approved invoices)
    let totalOpenItems = 0
    matches.forEach((match) => {
      // Skip voided matches entirely
      if (match.status === 'void') {
        return
      }

      if (match.status !== 'approved') {
        // Count the number of invoices in this match
        totalOpenItems += match.invoice_numbers?.length || 0
      }
    })

    // Total Unmatched Items (count of matches with 'unmatched' status)
    const totalUnmatchedItems = matches.filter(
      (match) => match.status === 'unmatched' && match.status !== 'void'
    ).length

    return {
      autoApprovePercentage,
      totalPastDue,
      totalOpenItems,
      totalUnmatchedItems,
    }
  }, [matches])

  // Calculate 14-day trends (fake but realistic)
  const trends = useMemo(() => {
    // Simulate day 1 values (14 days ago)
    const day1AutoApprove = generateAutoApprovePercentage(13)
    const day1PastDue = metrics.totalPastDue * 0.85 // Assume 15% increase
    const day1OpenItems = metrics.totalOpenItems * 0.9 // Assume 10% increase
    const day1Unmatched = metrics.totalUnmatchedItems * 1.2 // Assume 20% decrease

    return {
      autoApprovePercentage: calculateTrend(day1AutoApprove, metrics.autoApprovePercentage),
      totalPastDue: calculateTrend(day1PastDue, metrics.totalPastDue),
      totalOpenItems: calculateTrend(day1OpenItems, metrics.totalOpenItems),
      totalUnmatchedItems: calculateTrend(day1Unmatched, metrics.totalUnmatchedItems),
    }
  }, [metrics])

  // Calculate match counts and supplier breakdowns
  const { matchCounts, supplierBreakdowns } = useMemo(() => {
    // Group matches by supplier and status
    const supplierStatusMap = new Map<string, Map<string, number>>()

    matches.forEach((match) => {
      // Skip voided matches entirely
      if (match.status === 'void') {
        return
      }

      const supplierId = match.supplier_id
      const supplierName = match.supplier_name
      const status = match.status

      if (!supplierStatusMap.has(supplierId)) {
        supplierStatusMap.set(supplierId, new Map())
      }

      const statusMap = supplierStatusMap.get(supplierId)!
      statusMap.set('name', supplierName)
      statusMap.set(status, (statusMap.get(status) || 0) + 1)
    })

    // Calculate total counts
    let unmatchedCount = 0
    let unapprovedCount = 0
    let onHoldCount = 0

    // Build supplier breakdowns
    const unmatchedSuppliers: SupplierBreakdown[] = []
    const unapprovedSuppliers: SupplierBreakdown[] = []
    const onHoldSuppliers: SupplierBreakdown[] = []

    supplierStatusMap.forEach((statusMap, supplierId) => {
      const supplierName = statusMap.get('name') as string

      const unmatched = statusMap.get('unmatched') || 0
      const unapproved = statusMap.get('unapproved') || 0
      const hold = statusMap.get('hold') || 0

      if (unmatched > 0) {
        unmatchedCount += unmatched
        unmatchedSuppliers.push({ supplierId, supplierName, count: unmatched })
      }

      if (unapproved > 0) {
        unapprovedCount += unapproved
        unapprovedSuppliers.push({ supplierId, supplierName, count: unapproved })
      }

      if (hold > 0) {
        onHoldCount += hold
        onHoldSuppliers.push({ supplierId, supplierName, count: hold })
      }
    })

    // Sort suppliers by count (descending) and take top 5
    const sortByCount = (a: SupplierBreakdown, b: SupplierBreakdown) => b.count - a.count

    return {
      matchCounts: {
        unmatched: unmatchedCount,
        unapproved: unapprovedCount,
        onHold: onHoldCount,
      },
      supplierBreakdowns: {
        unmatched: unmatchedSuppliers.sort(sortByCount).slice(0, 5),
        unapproved: unapprovedSuppliers.sort(sortByCount).slice(0, 5),
        onHold: onHoldSuppliers.sort(sortByCount).slice(0, 5),
      },
    }
  }, [matches])

  return {
    metrics,
    trends,
    matchCounts,
    supplierBreakdowns,
  }
}
